// src/components/empresas/ConfigTributariaPage/ConfigTributariaContainer.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';

import ConfigTributariaPresentation from './ConfigTributariaPresentation';
import { useNotification } from '../../../hooks';
import { empresaService } from '../../../services/empresaService';

const FORM_INICIAL = {
  razon_social: '',
  giro: '',
  acteco: '',
  direccion: '',
  comuna: '',
  ciudad: '',
  fecha_resolucion_sii: '',
  nro_resolucion_sii: 0,
  ambiente_dte: 'certificacion',
  codigo_sucursal: '',
};

const ConfigTributariaContainer = () => {
  const { rutEmpresa } = useParams();
  const navigate = useNavigate();
  const { empresas } = useSelector(state => state.empresa);
  const { showNotification } = useNotification();

  const [empresa, setEmpresa] = useState(null);
  const [formData, setFormData] = useState(FORM_INICIAL);
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configurado, setConfigurado] = useState(false);

  useEffect(() => {
    const emp = empresas.find(e => e.rut === rutEmpresa) || null;
    setEmpresa(emp);
  }, [empresas, rutEmpresa]);

  useEffect(() => {
    if (!rutEmpresa) {
      navigate('/empresas');
      return;
    }
    setLoading(true);
    empresaService.getConfigTributaria(rutEmpresa)
      .then(data => {
        if (data.existe) {
          setConfigurado(true);
          setFormData({
            razon_social: data.razon_social || '',
            giro: data.giro || '',
            acteco: data.acteco != null ? String(data.acteco) : '',
            direccion: data.direccion || '',
            comuna: data.comuna || '',
            ciudad: data.ciudad || '',
            fecha_resolucion_sii: data.fecha_resolucion_sii || '',
            nro_resolucion_sii: data.nro_resolucion_sii ?? 0,
            ambiente_dte: data.ambiente_dte || 'certificacion',
            codigo_sucursal: data.codigo_sucursal || '',
          });
        } else {
          setConfigurado(false);
          setFormData(FORM_INICIAL);
        }
      })
      .catch(err => {
        console.error('Error al cargar config tributaria:', err);
        showNotification('Error al cargar configuración tributaria', 'error');
      })
      .finally(() => setLoading(false));
  }, [rutEmpresa]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  }, [formErrors]);

  const validate = useCallback(() => {
    const errors = {};
    if (!formData.giro?.trim()) errors.giro = 'El giro es obligatorio';
    if (!formData.acteco) errors.acteco = 'El código de actividad económica es obligatorio';
    if (!formData.direccion?.trim()) errors.direccion = 'La dirección es obligatoria';
    if (!formData.comuna?.trim()) errors.comuna = 'La comuna es obligatoria';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = {
        ...formData,
        acteco: formData.acteco ? parseInt(formData.acteco, 10) : null,
        nro_resolucion_sii: parseInt(formData.nro_resolucion_sii, 10) || 0,
        fecha_resolucion_sii: formData.fecha_resolucion_sii || null,
      };
      await empresaService.upsertConfigTributaria(rutEmpresa, payload);
      setConfigurado(true);
      showNotification('Configuración tributaria guardada correctamente', 'success');
    } catch (err) {
      console.error('Error al guardar config tributaria:', err);
      showNotification(err.message || 'Error al guardar configuración', 'error');
    } finally {
      setSaving(false);
    }
  }, [formData, rutEmpresa, validate, showNotification]);

  const handleVolver = useCallback(() => {
    navigate('/empresas');
  }, [navigate]);

  return (
    <ConfigTributariaPresentation
      empresa={empresa}
      rutEmpresa={rutEmpresa}
      formData={formData}
      formErrors={formErrors}
      loading={loading}
      saving={saving}
      configurado={configurado}
      onInputChange={handleInputChange}
      onSubmit={handleSubmit}
      onVolver={handleVolver}
    />
  );
};

export default ConfigTributariaContainer;
